// ═══════════════════════════════════════════════════════════════
// SHOW SPONSORSHIP SERVICE
// Path: src/services/showSponsorshipsService.js
//
// All Supabase operations for the Show Sponsorship module:
//   benefit_templates, shows, sponsorship_packages,
//   show_sponsorships, sponsorship_benefit_fulfillments
//
// No Supabase calls in component files — everything goes through here.
//
// Blueprint: CMM_Show_Sponsorship_Blueprint_v1_1.md
// Phase 2 deliverable
// ═══════════════════════════════════════════════════════════════

import { supabase } from "../lib/supabase";
import { debugLog } from "../utils/debugLog";

// ═══════════════════════════════════════════════════════════════
// SECTION 1: BENEFIT TEMPLATES
// ═══════════════════════════════════════════════════════════════

export const benefitTemplatesService = {
  // ── Get all templates, sorted by sort_order ───────────────────
  // Pass includeInactive = true for admin management views
  async getBenefitTemplates(includeInactive = false) {
    try {
      let query = supabase
        .from("benefit_templates")
        .select("*")
        .order("sort_order", { ascending: true })
        .order("name", { ascending: true });

      if (!includeInactive) {
        query = query.eq("is_active", true);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      debugLog.error("benefitTemplatesService.getBenefitTemplates", error);
      throw error;
    }
  },

  // ── Create a new benefit template ─────────────────────────────
  // Catches unique constraint → friendly error message
  async createBenefitTemplate(templateData, chapterId, userId) {
    try {
      const { data, error } = await supabase
        .from("benefit_templates")
        .insert({
          chapter_id: chapterId,
          name: templateData.name,
          description: templateData.description || null,
          category: templateData.category || null,
          sort_order: templateData.sortOrder || 0,
          is_active: templateData.isActive !== undefined ? templateData.isActive : true,
          created_by: userId,
        })
        .select("*")
        .single();

      if (error) {
        if (error.code === "23505") {
          throw new Error(`A benefit named "${templateData.name}" already exists.`);
        }
        throw error;
      }

      debugLog.action("benefitTemplatesService.create", { name: templateData.name });
      return data;
    } catch (error) {
      debugLog.error("benefitTemplatesService.createBenefitTemplate", error);
      throw error;
    }
  },

  // ── Update a benefit template ─────────────────────────────────
  // Name changes do NOT cascade to existing packages or fulfillments
  async updateBenefitTemplate(templateId, updates) {
    try {
      const row = {};
      if (updates.name !== undefined) row.name = updates.name;
      if (updates.description !== undefined) row.description = updates.description;
      if (updates.category !== undefined) row.category = updates.category;
      if (updates.sortOrder !== undefined) row.sort_order = updates.sortOrder;
      if (updates.isActive !== undefined) row.is_active = updates.isActive;

      const { data, error } = await supabase
        .from("benefit_templates")
        .update(row)
        .eq("id", templateId)
        .select("*")
        .single();

      if (error) {
        if (error.code === "23505") {
          throw new Error(`A benefit named "${updates.name}" already exists.`);
        }
        throw error;
      }

      debugLog.action("benefitTemplatesService.update", { templateId });
      return data;
    } catch (error) {
      debugLog.error("benefitTemplatesService.updateBenefitTemplate", error);
      throw error;
    }
  },

  // ── Deactivate (soft-delete) ──────────────────────────────────
  // Removes from picker; existing packages unaffected
  async deactivateBenefitTemplate(templateId) {
    try {
      const { data, error } = await supabase
        .from("benefit_templates")
        .update({ is_active: false })
        .eq("id", templateId)
        .select("*")
        .single();

      if (error) throw error;
      debugLog.action("benefitTemplatesService.deactivate", { templateId });
      return data;
    } catch (error) {
      debugLog.error("benefitTemplatesService.deactivateBenefitTemplate", error);
      throw error;
    }
  },

  // ── Delete (hard delete, admin only) ──────────────────────────
  // Caller should check for usage in active packages first via
  // getPackagesUsingBenefit() and show the guard modal
  async deleteBenefitTemplate(templateId) {
    try {
      const { error } = await supabase
        .from("benefit_templates")
        .delete()
        .eq("id", templateId);

      if (error) throw error;
      debugLog.action("benefitTemplatesService.delete", { templateId });
    } catch (error) {
      debugLog.error("benefitTemplatesService.deleteBenefitTemplate", error);
      throw error;
    }
  },

  // ── Batch reorder ─────────────────────────────────────────────
  // Accepts array of template IDs in desired display order
  async reorderBenefitTemplates(orderedIds) {
    try {
      const updates = orderedIds.map((id, index) => ({
        id,
        sort_order: index,
      }));

      // Supabase doesn't support batch update natively,
      // so we update each row individually
      for (const item of updates) {
        const { error } = await supabase
          .from("benefit_templates")
          .update({ sort_order: item.sort_order })
          .eq("id", item.id);

        if (error) throw error;
      }

      debugLog.action("benefitTemplatesService.reorder", { count: orderedIds.length });
    } catch (error) {
      debugLog.error("benefitTemplatesService.reorderBenefitTemplates", error);
      throw error;
    }
  },

  // ── Check which active packages use a benefit name ────────────
  // Used by delete guard modal before hard-deleting a template
  async getPackagesUsingBenefit(benefitName) {
    try {
      const { data, error } = await supabase
        .from("sponsorship_packages")
        .select("id, name, show_id")
        .eq("is_active", true);

      if (error) throw error;

      // Filter packages whose benefits JSONB array contains this name
      const matching = (data || []).filter((pkg) => {
        const benefits = pkg.benefits || [];
        return benefits.some((b) => b.name === benefitName);
      });

      return matching;
    } catch (error) {
      debugLog.error("benefitTemplatesService.getPackagesUsingBenefit", error);
      return [];
    }
  },
};


// ═══════════════════════════════════════════════════════════════
// SECTION 2: SHOWS
// ═══════════════════════════════════════════════════════════════

export const showsService = {
  // ── Get all non-archived shows ────────────────────────────────
  async getShows() {
    try {
      const { data, error } = await supabase
        .from("shows")
        .select("*")
        .neq("status", "archived")
        .order("open_date", { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      debugLog.error("showsService.getShows", error);
      throw error;
    }
  },

  // ── Get active shows only (for gift entry dropdown) ───────────
  async getActiveShows() {
    try {
      const { data, error } = await supabase
        .from("shows")
        .select("*")
        .in("status", ["upcoming", "active"])
        .order("open_date", { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      debugLog.error("showsService.getActiveShows", error);
      throw error;
    }
  },

  // ── Get all shows including archived (for admin toggle) ───────
  async getAllShows() {
    try {
      const { data, error } = await supabase
        .from("shows")
        .select("*")
        .order("open_date", { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      debugLog.error("showsService.getAllShows", error);
      throw error;
    }
  },

  // ── Create a show ─────────────────────────────────────────────
  async createShow(showData, chapterId, userId) {
    try {
      const { data, error } = await supabase
        .from("shows")
        .insert({
          chapter_id: chapterId,
          title: showData.title,
          season: showData.season,
          venue: showData.venue || null,
          open_date: showData.openDate || null,
          close_date: showData.closeDate || null,
          status: showData.status || "upcoming",
          description: showData.description || null,
          created_by: userId,
        })
        .select("*")
        .single();

      if (error) throw error;
      debugLog.action("showsService.create", { title: showData.title });
      return data;
    } catch (error) {
      debugLog.error("showsService.createShow", error);
      throw error;
    }
  },

  // ── Update a show ─────────────────────────────────────────────
  async updateShow(showId, updates) {
    try {
      const row = {};
      if (updates.title !== undefined) row.title = updates.title;
      if (updates.season !== undefined) row.season = updates.season;
      if (updates.venue !== undefined) row.venue = updates.venue;
      if (updates.openDate !== undefined) row.open_date = updates.openDate;
      if (updates.closeDate !== undefined) row.close_date = updates.closeDate;
      if (updates.status !== undefined) row.status = updates.status;
      if (updates.description !== undefined) row.description = updates.description;

      const { data, error } = await supabase
        .from("shows")
        .update(row)
        .eq("id", showId)
        .select("*")
        .single();

      if (error) throw error;
      debugLog.action("showsService.update", { showId });
      return data;
    } catch (error) {
      debugLog.error("showsService.updateShow", error);
      throw error;
    }
  },

  // ── Archive a show ────────────────────────────────────────────
  async archiveShow(showId) {
    try {
      const { data, error } = await supabase
        .from("shows")
        .update({ status: "archived" })
        .eq("id", showId)
        .select("*")
        .single();

      if (error) throw error;
      debugLog.action("showsService.archive", { showId });
      return data;
    } catch (error) {
      debugLog.error("showsService.archiveShow", error);
      throw error;
    }
  },
};


// ═══════════════════════════════════════════════════════════════
// SECTION 3: SPONSORSHIP PACKAGES
// ═══════════════════════════════════════════════════════════════

export const packagesService = {
  // ── Get packages ──────────────────────────────────────────────
  // If showId provided: show-specific + global (show_id IS NULL)
  // If omitted: all packages
  async getPackages(showId) {
    try {
      let query = supabase
        .from("sponsorship_packages")
        .select("*, shows (id, title, season)")
        .order("sort_order", { ascending: true })
        .order("min_amount", { ascending: true });

      if (showId) {
        query = query.or(`show_id.eq.${showId},show_id.is.null`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      debugLog.error("packagesService.getPackages", error);
      throw error;
    }
  },

  // ── Get qualifying packages for a gift amount ─────────────────
  // Returns active packages where min_amount <= amount,
  // sorted by min_amount DESC (highest tier first for auto-suggest)
  async getPackagesForAmount(amount, showId) {
    try {
      let query = supabase
        .from("sponsorship_packages")
        .select("*")
        .eq("is_active", true)
        .lte("min_amount", amount)
        .order("min_amount", { ascending: false });

      if (showId) {
        query = query.or(`show_id.eq.${showId},show_id.is.null`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    } catch (error) {
      debugLog.error("packagesService.getPackagesForAmount", error);
      return [];
    }
  },

  // ── Create a package ──────────────────────────────────────────
  async createPackage(packageData, chapterId, userId) {
    try {
      const { data, error } = await supabase
        .from("sponsorship_packages")
        .insert({
          chapter_id: chapterId,
          show_id: packageData.showId || null,
          name: packageData.name,
          min_amount: packageData.minAmount,
          benefits: packageData.benefits || [],
          sort_order: packageData.sortOrder || 0,
          is_active: packageData.isActive !== undefined ? packageData.isActive : true,
          notes: packageData.notes || null,
          created_by: userId,
        })
        .select("*, shows (id, title, season)")
        .single();

      if (error) throw error;
      debugLog.action("packagesService.create", { name: packageData.name });
      return data;
    } catch (error) {
      debugLog.error("packagesService.createPackage", error);
      throw error;
    }
  },

  // ── Update a package ──────────────────────────────────────────
  // Does NOT retroactively update existing sponsorship fulfillment rows
  async updatePackage(packageId, updates) {
    try {
      const row = {};
      if (updates.showId !== undefined) row.show_id = updates.showId;
      if (updates.name !== undefined) row.name = updates.name;
      if (updates.minAmount !== undefined) row.min_amount = updates.minAmount;
      if (updates.benefits !== undefined) row.benefits = updates.benefits;
      if (updates.sortOrder !== undefined) row.sort_order = updates.sortOrder;
      if (updates.isActive !== undefined) row.is_active = updates.isActive;
      if (updates.notes !== undefined) row.notes = updates.notes;

      const { data, error } = await supabase
        .from("sponsorship_packages")
        .update(row)
        .eq("id", packageId)
        .select("*, shows (id, title, season)")
        .single();

      if (error) throw error;
      debugLog.action("packagesService.update", { packageId });
      return data;
    } catch (error) {
      debugLog.error("packagesService.updatePackage", error);
      throw error;
    }
  },

  // ── Delete a package (admin only) ─────────────────────────────
  async deletePackage(packageId) {
    try {
      const { error } = await supabase
        .from("sponsorship_packages")
        .delete()
        .eq("id", packageId);

      if (error) throw error;
      debugLog.action("packagesService.delete", { packageId });
    } catch (error) {
      debugLog.error("packagesService.deletePackage", error);
      throw error;
    }
  },
};


// ═══════════════════════════════════════════════════════════════
// SECTION 4: SHOW SPONSORSHIPS
// ═══════════════════════════════════════════════════════════════

const SPONSORSHIP_SELECT = `
  *,
  shows (id, title, season),
  sponsorship_packages (id, name, min_amount),
  members (id, first_name, last_name, credentialed_full_name)
`;

export const sponsorshipsService = {
  // ── Get sponsorships with filters ─────────────────────────────
  // filters: { showId, memberId, status }
  // status: "pending" = has unfulfilled benefits, "complete" = all fulfilled
  async getSponsorships(filters = {}) {
    try {
      let query = supabase
        .from("show_sponsorships")
        .select(`${SPONSORSHIP_SELECT}, sponsorship_benefit_fulfillments (id, fulfilled_at)`)
        .order("created_at", { ascending: false });

      if (filters.showId) query = query.eq("show_id", filters.showId);
      if (filters.memberId) query = query.eq("member_id", filters.memberId);

      const { data, error } = await query;
      if (error) throw error;

      // Compute fulfillment summary for each sponsorship
      let results = (data || []).map((row) => {
        const fulfillments = row.sponsorship_benefit_fulfillments || [];
        const totalBenefits = fulfillments.length;
        const fulfilledCount = fulfillments.filter((f) => f.fulfilled_at !== null).length;
        const isComplete = totalBenefits > 0 && fulfilledCount === totalBenefits;

        return {
          ...row,
          totalBenefits,
          fulfilledCount,
          isComplete,
          memberName: row.members
            ? row.members.credentialed_full_name ||
              `${row.members.first_name} ${row.members.last_name}`.trim()
            : null,
        };
      });

      // Apply status filter client-side (requires fulfillment counts)
      if (filters.status === "pending") {
        results = results.filter((r) => !r.isComplete);
      } else if (filters.status === "complete") {
        results = results.filter((r) => r.isComplete);
      }

      return results;
    } catch (error) {
      debugLog.error("sponsorshipsService.getSponsorships", error);
      throw error;
    }
  },

  // ── Get all sponsorships for a member ─────────────────────────
  // Used by DonorDetailView Sponsorships tab
  async getSponsorshipsByMember(memberId) {
    try {
      const { data, error } = await supabase
        .from("show_sponsorships")
        .select(`
          *,
          shows (id, title, season, open_date),
          sponsorship_packages (id, name, min_amount),
          sponsorship_benefit_fulfillments (id, benefit_name, sort_order, is_custom, fulfilled_at, fulfilled_by, notes)
        `)
        .eq("member_id", memberId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      return (data || []).map((row) => {
        const fulfillments = row.sponsorship_benefit_fulfillments || [];
        const totalBenefits = fulfillments.length;
        const fulfilledCount = fulfillments.filter((f) => f.fulfilled_at !== null).length;

        return {
          ...row,
          totalBenefits,
          fulfilledCount,
          isComplete: totalBenefits > 0 && fulfilledCount === totalBenefits,
        };
      });
    } catch (error) {
      debugLog.error("sponsorshipsService.getSponsorshipsByMember", error);
      throw error;
    }
  },

  // ── Create a sponsorship ──────────────────────────────────────
  // Logical transaction:
  //   1. Insert show_sponsorship with package_snapshot
  //   2. Bulk-insert fulfillment rows from package benefits
  async createSponsorship(sponsorshipData, chapterId, userId) {
    try {
      // 1. Fetch the package to snapshot its benefits
      const { data: pkg, error: pkgError } = await supabase
        .from("sponsorship_packages")
        .select("*")
        .eq("id", sponsorshipData.packageId)
        .single();

      if (pkgError) throw pkgError;

      const packageSnapshot = {
        name: pkg.name,
        min_amount: pkg.min_amount,
        benefits: pkg.benefits || [],
      };

      // 2. Insert the sponsorship
      const { data: sponsorship, error: sponsorshipError } = await supabase
        .from("show_sponsorships")
        .insert({
          chapter_id: chapterId,
          show_id: sponsorshipData.showId || null,
          package_id: sponsorshipData.packageId,
          gift_id: sponsorshipData.giftId,
          member_id: sponsorshipData.memberId,
          credit_as_printed: sponsorshipData.creditAsPrinted,
          package_snapshot: packageSnapshot,
          notes: sponsorshipData.notes || null,
          created_by: userId,
        })
        .select("*")
        .single();

      if (sponsorshipError) throw sponsorshipError;

      // 3. Bulk-insert fulfillment rows from package benefits
      const benefits = packageSnapshot.benefits || [];
      if (benefits.length > 0) {
        const fulfillmentRows = benefits.map((benefit) => ({
          chapter_id: chapterId,
          sponsorship_id: sponsorship.id,
          benefit_name: benefit.name,
          sort_order: benefit.sort_order || 0,
          is_custom: false,
        }));

        const { error: fulfillmentError } = await supabase
          .from("sponsorship_benefit_fulfillments")
          .insert(fulfillmentRows);

        if (fulfillmentError) {
          debugLog.error(
            "sponsorshipsService.createSponsorship — fulfillment insert failed",
            fulfillmentError
          );
          // Sponsorship exists but fulfillments failed — not rolled back
          // Can be manually fixed from ShowsView
        }
      }

      debugLog.action("sponsorshipsService.create", {
        sponsorshipId: sponsorship.id,
        showId: sponsorshipData.showId,
        packageName: pkg.name,
        benefitCount: benefits.length,
      });

      return sponsorship;
    } catch (error) {
      debugLog.error("sponsorshipsService.createSponsorship", error);
      throw error;
    }
  },

  // ── Delete a sponsorship (admin only) ─────────────────────────
  // CASCADE deletes fulfillment rows automatically
  async deleteSponsorship(sponsorshipId) {
    try {
      const { error } = await supabase
        .from("show_sponsorships")
        .delete()
        .eq("id", sponsorshipId);

      if (error) throw error;
      debugLog.action("sponsorshipsService.delete", { sponsorshipId });
    } catch (error) {
      debugLog.error("sponsorshipsService.deleteSponsorship", error);
      throw error;
    }
  },
};


// ═══════════════════════════════════════════════════════════════
// SECTION 5: BENEFIT FULFILLMENTS
// ═══════════════════════════════════════════════════════════════

export const fulfillmentsService = {
  // ── Get all fulfillments for a sponsorship ────────────────────
  async getFulfillments(sponsorshipId) {
    try {
      const { data, error } = await supabase
        .from("sponsorship_benefit_fulfillments")
        .select("*")
        .eq("sponsorship_id", sponsorshipId)
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      debugLog.error("fulfillmentsService.getFulfillments", error);
      throw error;
    }
  },

  // ── Mark a benefit as fulfilled ───────────────────────────────
  async markFulfilled(fulfillmentId, userId, notes) {
    try {
      const row = {
        fulfilled_at: new Date().toISOString(),
        fulfilled_by: userId,
      };
      if (notes !== undefined) row.notes = notes;

      const { data, error } = await supabase
        .from("sponsorship_benefit_fulfillments")
        .update(row)
        .eq("id", fulfillmentId)
        .select("*")
        .single();

      if (error) throw error;
      debugLog.action("fulfillmentsService.markFulfilled", { fulfillmentId });
      return data;
    } catch (error) {
      debugLog.error("fulfillmentsService.markFulfilled", error);
      throw error;
    }
  },

  // ── Unmark a benefit (clear fulfillment) ──────────────────────
  async unmarkFulfilled(fulfillmentId) {
    try {
      const { data, error } = await supabase
        .from("sponsorship_benefit_fulfillments")
        .update({
          fulfilled_at: null,
          fulfilled_by: null,
          notes: null,
        })
        .eq("id", fulfillmentId)
        .select("*")
        .single();

      if (error) throw error;
      debugLog.action("fulfillmentsService.unmarkFulfilled", { fulfillmentId });
      return data;
    } catch (error) {
      debugLog.error("fulfillmentsService.unmarkFulfilled", error);
      throw error;
    }
  },

  // ── Mark all benefits fulfilled for a sponsorship ─────────────
  // Admin/Manager quick action
  async markAllFulfilled(sponsorshipId, userId) {
    try {
      const { data, error } = await supabase
        .from("sponsorship_benefit_fulfillments")
        .update({
          fulfilled_at: new Date().toISOString(),
          fulfilled_by: userId,
        })
        .eq("sponsorship_id", sponsorshipId)
        .is("fulfilled_at", null)
        .select("*");

      if (error) throw error;
      debugLog.action("fulfillmentsService.markAllFulfilled", {
        sponsorshipId,
        count: (data || []).length,
      });
      return data || [];
    } catch (error) {
      debugLog.error("fulfillmentsService.markAllFulfilled", error);
      throw error;
    }
  },

  // ── Add a custom benefit to an existing sponsorship ───────────
  // Appended with sort_order = max + 1, is_custom = true
  async addCustomBenefit(sponsorshipId, benefitName, chapterId, userId) {
    try {
      // Get current max sort_order
      const { data: existing, error: fetchError } = await supabase
        .from("sponsorship_benefit_fulfillments")
        .select("sort_order")
        .eq("sponsorship_id", sponsorshipId)
        .order("sort_order", { ascending: false })
        .limit(1);

      if (fetchError) throw fetchError;

      const nextSortOrder = existing && existing.length > 0
        ? existing[0].sort_order + 1
        : 0;

      const { data, error } = await supabase
        .from("sponsorship_benefit_fulfillments")
        .insert({
          chapter_id: chapterId,
          sponsorship_id: sponsorshipId,
          benefit_name: benefitName,
          sort_order: nextSortOrder,
          is_custom: true,
        })
        .select("*")
        .single();

      if (error) throw error;
      debugLog.action("fulfillmentsService.addCustomBenefit", {
        sponsorshipId,
        benefitName,
      });
      return data;
    } catch (error) {
      debugLog.error("fulfillmentsService.addCustomBenefit", error);
      throw error;
    }
  },
};
